import React from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { trim } from 'lodash';
import {
    makeStyles,
    TextField,
    Dialog,
    DialogContent,
    Button,
} from '@material-ui/core';
import { useMutation } from '@apollo/client';
import StringUtils from 'lib/StringUtils';
import MessageUtils from 'utils/MessageUtils';
import ModalUtils from 'utils/ModalUtils';
import InventoryMutation from 'services/graphQL/mutate/InventoryMutation';
import DialogAppBar from 'components/widgets/modal/DialogAppBar';
import DialogActions from 'components/widgets/modal/DialogActions';
import VehicleNoteStyle from 'styles/widgets/inventory/VehicleNoteStyle';
import ButtonStyles from 'styles/theme/Button';

const useStyles = makeStyles((theme) => VehicleNoteStyle.notePanel(theme));
const buttonStyles = makeStyles((theme) => ButtonStyles.getStyle(theme));

const VehicleNoteEditDialog = ({
    canWrite,
    embedded,
    openDialog,
    dialogLabel,
    selectedNote,
    state,
    setState,
    stockNumber,
    version,
    refetchNotes,
    onUpdateVersion,
    onEditNote,
    placeholder,
}) => {
    const classes = { ...useStyles(), ...buttonStyles() };

    const [updateVehicle, { loading: savingVehicle }] = useMutation(InventoryMutation.UPDATE_VEHICLE);
    const [updateVehicleNotes, { loading: savingVehicleNotes }] = useMutation(InventoryMutation.UPDATE_VEHICLE_NOTES);

    const onChange = (name, value) => {
        setState((prevState) => ({
            ...prevState,
            [name]: value,
        }));
    };

    const onSave = async () => {
        if (canWrite) {
            try {
                const newNote = trim(state[selectedNote]);
                let isSuccess = false;

                if (selectedNote === 'notes') {
                    const variables = {
                        stockNumber,
                        notes: newNote,
                        version,
                    };

                    const response = await updateVehicleNotes({ variables });
                    isSuccess = Boolean(response?.data?.updateVehicleNotes);
                } else {
                    const variables = {
                        stockNumber,
                        input: {
                            [selectedNote]: newNote,
                            version,
                        },
                    };

                    const response = await updateVehicle({ variables });
                    isSuccess = Boolean(response?.data?.updateVehicle);
                }

                if (isSuccess) {
                    refetchNotes();
                    onUpdateVersion(version + 1);

                    ModalUtils.successMessage(null, 'Notes updated successfully!');
                } else {
                    ModalUtils.errorMessage(null, MessageUtils.getGenericError('save', 'Vehicle Notes'));
                }
            } catch (ex) {
                ModalUtils.errorMessage(null, ex.message);
            }
        }
    };

    const getNoteField = (rows) => (
        <TextField
            placeholder={placeholder}
            multiline
            rows={rows}
            margin="normal"
            variant="outlined"
            value={StringUtils.isEmpty(state[selectedNote]) ? '' : state[selectedNote]}
            className={clsx(classes.textField, classes.noteField)}
            onChange={(event) => onChange(selectedNote, event.target.value)}
        />
    );

    const saving = savingVehicle || savingVehicleNotes;
    return embedded ? (
        <div className={classes.noteParent}>
            {getNoteField(2)}
            <Button
                disabled={!canWrite || saving}
                onClick={onSave}
                className={classes.containedInfo}
            >
                Save
            </Button>
        </div>
    ) : (
        <Dialog
            open={openDialog}
            maxWidth="sm"
            fullWidth
            className={classes.dialog}
        >
            <DialogAppBar
                appBarClassName={classes.AppBar}
                title={dialogLabel}
                onClose={() => onEditNote()}
            />
            <DialogContent className={classes.dialogContent}>
                {getNoteField(4)}
            </DialogContent>
            <DialogActions
                titlePrimary="Save"
                onClickPrimary={onSave}
                disableSecondaryButton={saving}
                disablePrimaryButton={saving}
                titleSecondary="Cancel"
                onClickSecondary={() => onEditNote()}
                variant="contained"
            />
        </Dialog>
    );
};

VehicleNoteEditDialog.defaultProps = {
    embedded: false,
    openDialog: false,
    dialogLabel: '',
    selectedNote: '',
    stockNumber: 0,
    version: 0,
    placeholder: 'Notes',
    onEditNote: () => null,
};

VehicleNoteEditDialog.propTypes = {
    embedded: PropTypes.bool,
    openDialog: PropTypes.bool,
    dialogLabel: PropTypes.string,
    state: PropTypes.object.isRequired,
    setState: PropTypes.func.isRequired,
    selectedNote: PropTypes.string,
    stockNumber: PropTypes.number,
    version: PropTypes.number,
    onUpdateVersion: PropTypes.func.isRequired,
    refetchNotes: PropTypes.func.isRequired,
    onEditNote: PropTypes.func,
    canWrite: PropTypes.bool.isRequired,
    placeholder: PropTypes.string,
};

export default VehicleNoteEditDialog;
